import React, { useState, useCallback, useEffect } from "react";
import { getStandardHeaderView } from "./ReactTable";
import translate from "../../../i18n/translate";
import { Button, CircularProgress, Typography } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { USER } from "../../../redux/constants";
import { createVocabularyNewWordList, getVocabularyContents } from "../../../redux/actions/vocabularyCenterAction";
import { ErrorOutlineOutlinedIcon } from "../../common/icon";

export function useAddWordListDialog() {
  const WORDLIST_REGEX = /^[a-zA-Z0-9 ]+$/;

  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [newWordList, setNewWordList] = useState("");
  const [showError, setShowError] = React.useState<boolean>(false);
  const [alreadyExistsMessage, setAlreadyExistsMessage] = React.useState<boolean>(false);
  const { peopleInformation, common, vocabularyContents } = useSelector((state: any) => state);

  useEffect(() => {
    if (peopleInformation?.data && common?.lgId) {
      const { token, accountId } = peopleInformation.data.getpeopleInformation;
      if (vocabularyContents.isWordListCreated) {
        dispatch(getVocabularyContents(common.lgId, token, parseInt(accountId, 10)));
        closeDialog();
      }
    }
  }, [vocabularyContents.isWordListCreated]);

  const openDialog = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setNewWordList("");
  }, []);

  const handleWordListActions = () => {
    const userWordList = vocabularyContents.topicsListData?.filter(
      (topicLevelDetail: any) => topicLevelDetail.headerTitle === "My word lists",
    );
    const isExists = userWordList[0]?.listDetails
      ? userWordList[0].listDetails.findIndex((listDetails: any) => listDetails.wordlist === newWordList)
      : -1;
    if (isExists !== -1) {
      setAlreadyExistsMessage(true);
    } else {
      dispatch(createVocabularyNewWordList(peopleInformation.data.getpeopleInformation.token, newWordList, USER));
    }
    setNewWordList("");
  };

  const getAddWordList = useCallback(() => {
    return (
      <>
        {getStandardHeaderView(translate("vocab_center_add_wordlist"), () => {
          setIsOpen(false);
          setNewWordList("");
        })}
        <div className="add-view-contents-box">
          <Typography className="add-view-edit-label">{translate("vocab_center_new_wordlist")}:</Typography>
          <input
            type="text"
            className="word-list-input"
            value={newWordList}
            onChange={(e) => {
              setShowError(!WORDLIST_REGEX.test(e.target.value));
              setNewWordList(e.target.value);
              setAlreadyExistsMessage(false);
            }}
            maxLength={40}
          />
        </div>
        {showError && <p className="show-error-message">Accepted Characters - A-Z, a-z, 0-9</p>}
        {alreadyExistsMessage && (
          <div className="wordlist-already-exists-container">
            <ErrorOutlineOutlinedIcon className="alert-icon" />
            <p className="already-exists-label">{translate("vocab_center_wordlist_exists")}</p>
          </div>
        )}
        <div className="word-list-action-box">
          <Button
            variant="contained"
            className="word-list-action-button"
            onClick={handleWordListActions}
            disabled={
              alreadyExistsMessage ||
              vocabularyContents.isWordListCreating ||
              vocabularyContents.isWordListUpdating ||
              newWordList.length === 0 ||
              showError
            }
          >
            {vocabularyContents.isWordListUpdating || vocabularyContents.isWordListCreating ? (
              <CircularProgress size="20" />
            ) : (
              translate("vocab_center_add_text")
            )}
          </Button>
          <Button
            variant="contained"
            className="word-list-action-button"
            onClick={() => {
              setIsOpen(false);
              setNewWordList("");
              setShowError(false);
              setAlreadyExistsMessage(false);
            }}
          >
            {translate("vocab_center_close_text")}
          </Button>
        </div>
      </>
    );
  }, [newWordList]);

  return { isOpen, newWordList, setNewWordList, openDialog, closeDialog, getAddWordList };
}
